import vue2Dropzone from "vue2-dropzone";

export default {
    components:{ vueDropzone: vue2Dropzone },
    data(){
        return {
            dropzoneOptions: {
                url: "required_url",
                acceptedFiles: "image/*",
                thumbnailWidth: 160,
                thumbnailHeight: 160,
                maxFilesize: 10, // 10MB
                manuallyAddFile: true,
                autoProcessQueue: false,
                maxFiles: 100,
                previewTemplate: this.template()
            },
            asset_images_previewElements: {},
        }
    },
    created: function () {
    },
    computed: {
        /**
         * T32: how many files this form has queued, for r11's `--bar` state. The drop area is the box it is
         * today while this is 0 and shrinks to a 46px bar the moment it is not. Named with its own prefix so
         * it can collide with nothing any of the call sites declares, and additive: no call site has to pass
         * anything and none was edited for it.
         */
        ftd_queued_count() {
            let queued = this.addForm && this.addForm.filled_asset_images ? this.addForm.filled_asset_images : null;
            return queued && queued.length ? queued.length : 0;
        },
        /**
         * T32: the drop area's second line. Waiting it is the sentence every drop area of the site has
         * today, word for word; once something is queued it is r11's count on the bar
         * (`r11-images-dropzone-media.html:205`, "3 files queued").
         */
        ftd_drop_sub() {
            let queued = this.ftd_queued_count;
            if (!queued) {
                return '... or click to select a file from your computer';
            }
            return queued + (queued === 1 ? ' file queued' : ' files queued');
        }
    },
    mounted() {
        this.ftdAttachQueueGrids();
        this.ftdDecorateDropBlocks();
    },
    updated() {
        this.ftdAttachQueueGrids();
        this.ftdDecorateDropBlocks();
    },
    methods: {
        /**
         * T32: THE `.ftd` ANCESTOR, decided per drop block and not by a blanket wrapper.
         *
         * The approved sheet writes 246 of its rules `.ftd .ftd-something`, so a redrawn drop area with no
         * `.ftd` above it takes the layout and silently loses every control rule. But `.ftd` is a SIZE
         * CONTAINER, so a nested one answers the wrong width and a blanket wrapper is a defect of its own.
         * `ThreeDotsMenu.vue` (T17, merged) settled the shape for a component that may or may not have one:
         * read the page once mounted and carry `ftd` only where there is none above. This is that reading,
         * done per drop block rather than per component, because one form can draw two of them and a modal
         * shell may already carry `ftd` for some of them and not for others.
         *
         * The wrapper is kept as tight round the drop block as the look allows, which is Amendment 10 item
         * 5's standing rule while the sheet's `list-style: revert` is still in it.
         */
        ftdDecorateDropBlocks() {
            try {
                if (typeof document === 'undefined' || !document.querySelectorAll) { return; }
                let blocks = document.querySelectorAll('[data-ftd-block=""]');
                for (let i = 0; i < blocks.length; i++) {
                    let block = blocks[i];
                    block.setAttribute('data-ftd-block', 'read');
                    if (!block.parentElement || typeof block.parentElement.closest !== 'function') { continue; }
                    if (block.parentElement.closest('.ftd')) { continue; }
                    block.classList.add('ftd');
                    let plate = (typeof window !== 'undefined' && window.COLOUR_PLATE) ? window.COLOUR_PLATE : null;
                    if (!plate) { continue; }
                    let variables = {
                        '--ftd-primary': plate.primary_colour,
                        '--ftd-accent': plate.accent_colour,
                        '--ftd-primary-soft': plate.soft_primary_colour,
                        '--ftd-accent-soft': plate.soft_accent_colour
                    };
                    Object.keys(variables).forEach(function (name) {
                        if (variables[name]) { block.style.setProperty(name, variables[name]); }
                    });
                }
            } catch (e) {
                // a drop block that cannot read the page keeps `.ftd`'s own fall-back colours
            }
        },
        /**
         * T32: THE QUEUED GRID.
         *
         * Dropzone appends every preview tile to `previewsContainer`, which defaults to the element it
         * listens on (`node_modules/dropzone/dist/dropzone.js:1125-1132`). r11 draws the queue as a SIBLING
         * of the drop area, because once a file is queued the drop area shrinks to a 46px bar and the tiles
         * take the room it gives up: a tile appended inside the bar could be neither the bar nor the wall.
         *
         * So every drop block this piece redrew carries
         *   <div class="ftd-tiles-grid ftd-r11-queue" data-ftd-queue></div>
         * straight after its `<vue-dropzone class="ftd-drop">`, and this points that instance's
         * `previewsContainer` at it once the DOM exists. It is a post-init assignment, which Dropzone reads
         * at append time (`:765-769`), so no option, no contract and no call site changes; a drop block that
         * draws no `[data-ftd-queue]` keeps Dropzone's own default container and is untouched.
         *
         * One consequence, written down because a merged rule turns on it: `addedfile` adds `dz-started` to
         * the element only while the element IS the previews container (`:761-763`), so a redrawn drop area
         * never takes that class and T23's interim `.ftd .ftd-drop.dz-started` rule
         * (`public/css/r2-add-asset-page.css:161`) stops matching rather than being cancelled. The bar is
         * drawn by `.ftd-drop--bar` instead. `dz-drag-hover` is untouched and T23's rule for it still holds.
         */
        ftdAttachQueueGrids() {
            try {
                if (typeof document === 'undefined' || !document.querySelectorAll) { return; }
                let grids = document.querySelectorAll('[data-ftd-queue]');
                for (let i = 0; i < grids.length; i++) {
                    let grid = grids[i];
                    let drop = grid.previousElementSibling;
                    while (drop && !drop.dropzone) { drop = drop.previousElementSibling; }
                    if (!drop || !drop.dropzone || drop.dropzone.previewsContainer === grid) { continue; }
                    drop.dropzone.previewsContainer = grid;
                    drop.classList.remove('dz-started');
                }
            } catch (e) {
                // a queue that cannot be reached leaves Dropzone with its own default container: the tiles
                // draw inside the drop area exactly as they do today, which is a look, never a broken upload
            }
        },
        organizeAssetImages: function (asset_images) {
            let asset_images_copy = [...asset_images];
            asset_images_copy = asset_images_copy.map(function (image) {
                let new_image = {
                    item_type: image.item_type,
                    uuid: image.uuid,
                    is_featured: image.is_featured,
                    add_to_ap: image.add_to_ap,
                    image_title: image.image_title,
                    image_description: image.image_description,
                    width: image.width,
                    height: image.height,
                    key: image.key,
                    name: image.name,
                }
                if (image.hasOwnProperty('id')) {
                    new_image.id = image.id;
                }

                return new_image;
            });
            return asset_images_copy;
        },
        checkAndGetFiles(filled_asset_images){
            var j, len, file;
            var allfiles = []

            if (this.addForm === undefined){
                this.addForm = {};
            }
            if (filled_asset_images !== undefined){
                this.addForm.filled_asset_images = filled_asset_images;
            }

            if (this.addForm.hasOwnProperty('filled_asset_images') && this.addForm.filled_asset_images){
                let finished_all_uploads = this.addForm.filled_asset_images.every(x => x.upload_progress === 100);
                if (!finished_all_uploads){
                    this.buttonkey++;
                    this.presentToast("Error",
                        'Uploading images, Please wait.',
                        'info',
                        3000,
                        true,
                        true);
                    return false
                }

                for (const filledAssetImagesKey in this.addForm.filled_asset_images) {
                    file = this.addForm.filled_asset_images[filledAssetImagesKey];

                    var previewElement;
                    if (file && file.hasOwnProperty('id') && this.asset_images_previewElements[file.id]){
                        previewElement = this.asset_images_previewElements[file.id];
                    }else{
                        previewElement = file.previewElement;
                    }

                    if (previewElement){
                        var is_featuredRef = previewElement.querySelectorAll("[data-dz-is_featured]");
                        if (is_featuredRef){
                            var is_featuredElement = is_featuredRef[0];
                            if (is_featuredElement){
                                file.is_featured = is_featuredElement.checked
                            }

                        }

                        var add_to_apRef = previewElement.querySelectorAll("[data-dz-add_to_ap]");
                        if (add_to_apRef){
                            var add_to_apElement = add_to_apRef[0];
                            if (add_to_apElement){
                                file.add_to_ap = add_to_apElement.checked
                            }
                        }

                        let image_titleRef = previewElement.querySelectorAll("[data-dz-image_title]");
                        if (image_titleRef){
                            let image_titleElement = image_titleRef[0];
                            if (image_titleElement){
                                file.image_title = image_titleElement.value
                            }
                        }

                        let image_descriptionRef = previewElement.querySelectorAll("[data-dz-image_description]");
                        if (image_descriptionRef){
                            let image_descriptionElement = image_descriptionRef[0];
                            if (image_descriptionElement){
                                file.image_description = image_descriptionElement.value
                            }
                        }
                    }

                    /*
                     * E7 (hand-off 2026-09-25, entry 7, the dated line of 08:23Z): THE TILE'S DOM NODE IS NOT
                     * POSTED. Every file returned here is posted inside `asset_images`, and Dropzone's file
                     * carries its tile as `previewElement`, and again as `previewTemplate` (the same node,
                     * Dropzone's "backwards compatibility" alias, `node_modules/dropzone/dist/dropzone.js:767`);
                     * JSON serialises each as `{}` and nothing on the server reads either. The file object
                     * itself is left exactly as it is (Dropzone still removes the tile through it, and every
                     * caller still reads its `name`, `size` and the rest); only what JSON writes for it loses
                     * those two keys, through a non-enumerable `toJSON` that answers the file's own enumerable
                     * keys, which is what JSON wrote before, less the tile. Written inline, not as a method,
                     * because harnesses call this function detached from the component (KNOWN.md rule 5).
                     * An object that already answers `toJSON`, or cannot take a property, is left alone.
                     * SHARED: every dialogue that posts through this mixin.
                     */
                    if (file && typeof file === 'object' && typeof file.toJSON !== 'function') {
                        try {
                            Object.defineProperty(file, 'toJSON', {
                                configurable: true,
                                writable: true,
                                enumerable: false,
                                value: function () {
                                    let posted = {};
                                    Object.keys(this).forEach((key) => {
                                        if (key !== 'previewElement' && key !== 'previewTemplate') { posted[key] = this[key]; }
                                    });
                                    return posted;
                                }
                            });
                        } catch (e) {
                            // a frozen object is posted as it is today
                        }
                    }

                    allfiles.push(file);
                }
            }

            return allfiles;
        },
        /**
         * T32-tiles: THE QUEUED TILE, r11's `.ftd-ptile` and its EIGHT ROWS.
         * (walk 2026-09-18 `tasks-T32-T33.md`, "Wanted" block 3; the reference is
         * `mock/design-walk/r11-images-dropzone-media.html`, the second screen, read class by class.)
         *
         * IT IS STILL A STRING, and that was settled by `T32-dropzone` and not re-opened here: Dropzone
         * parses `previewTemplate` with `Dropzone.createElement(...)` outside Vue
         * (`node_modules/dropzone/dist/dropzone.js`, the `addedfile` handler), so the tile cannot be a Vue
         * component. `T32-dropzone` changed not one character inside this method and handed its whole inside
         * to `T32-tiles`; nothing else in this file is touched by that hand-over.
         *
         * THE EIGHT ROWS, which the queue owns as a track list so a row of tiles is ONE BAND: the picture,
         * then the body's seven - the file line, Image Title, Image Description, the two ticks, the bar, the
         * message and Remove file. The body must therefore have exactly seven children and the order below
         * is the band's order.
         *
         * EVERY `data-dz-*` ATTRIBUTE IS A CONTRACT and every one of them is here, on an element of the same
         * kind as before: `data-dz-thumbnail-bg` (this file's `thumbnail()`), `data-dz-image_title`,
         * `data-dz-image_description`, `data-dz-is_featured` and `data-dz-add_to_ap` (`checkAndGetFiles`),
         * `data-dz-uploadprogress`, `data-dz-errormessage` and `data-dz-remove` (Dropzone's own). Two are
         * NEW and they are Dropzone's own, costing no server call and adding no field to any record:
         * `data-dz-name` and `data-dz-size`, which draw the file's own name and size (Log 369).
         *
         * NO `dz-*` CLASS SURVIVES on this markup, deliberately. The site still hides `.dz-error-mark`
         * unscoped (`resources/sass/vue_dropzone_custom.scss`, its last rule), `T32-dropzone`'s three interim
         * rules hide `.dz-success-mark` and force `.dz-image` to 100 x 100 in three homes, and Bootstrap's
         * `col-3` sized the old root: a tile that kept any of those names would be dressed twice.
         *
         * WHICH STATES THIS APPLICATION ACTUALLY REACHES, re-measured in fix round 2 (the build note's "Fix
         * round 2", finding 1). `dz-error` IS reached: `accept()` refuses a file and `_errorProcessing()`
         * adds it. `dz-success` is NOT, on any dropzone of this site: every instance is built
         * `autoProcessQueue: false` and nothing in `resources/` ever calls `processQueue()`, so Dropzone
         * never uploads and never emits `success`; the upload is `Vapor.store()` in `usermeta.mixin.js`
         * `saveFileToS3()`, which emits nothing to Dropzone, and `vue2Dropzone`'s `manuallyAddFile` emits
         * `addedfile`, `thumbnail` and `complete` only. So the FINISHED state is read off the one signal the
         * application really writes: `aria-valuenow="100"` on `[data-dz-uploadprogress]`, set by
         * `saveFileToS3()`'s progress callback and by `fileAdded()` below. That is why the role and the three
         * `aria-value*` attributes sit on the FILL and not on the track: they are what the two writers write,
         * and on the track they would have announced 0 for ever (the base had them on the same element).
         */
        template: function () {
            return `<div class="ftd-ptile">
                            <div class="ftd-ptile__img">
                                <i class="bx bx-image"></i>
                                <div class="ftd-r11-thumb" data-dz-thumbnail-bg></div>
                                <span class="ftd-ptile__mark ftd-ptile__mark--ok ftd-r11-tilemark ftd-r11-tilemark--ok" title="Uploaded"><i class="bx bx-check" aria-hidden="true"></i><span class="ftd-sr">Uploaded</span></span>
                                <span class="ftd-ptile__mark ftd-ptile__mark--bad ftd-r11-tilemark ftd-r11-tilemark--bad" title="Failed"><i class="bx bx-x" aria-hidden="true"></i><span class="ftd-sr">Failed</span></span>
                            </div>
                            <div class="ftd-ptile__body">
                                <span class="ftd-r11-file"><span class="ftd-ptile__name" data-dz-name></span><span class="ftd-ptile__size" data-dz-size></span></span>
                                <div class="ftd-field">
                                    <label class="ftd-field__label">Image Title</label>
                                    <input class="ftd-input" type="text" data-dz-image_title name="image_title" placeholder="Image Title" />
                                </div>
                                <div class="ftd-field">
                                    <label class="ftd-field__label">Image Description</label>
                                    <textarea class="ftd-input" rows="2" data-dz-image_description name="image_description" placeholder="Image Description"></textarea>
                                </div>
                                <div class="ftd-checks ftd-checks--col">
                                    <label class="ftd-check"><input type="checkbox" data-dz-is_featured name="is_featured[]" /> Is Featured?</label>
                                    <label class="ftd-check"><input type="checkbox" data-dz-add_to_ap name="add_to_ap[]" /> Add to Asset Passport?</label>
                                </div>
                                <div class="ftd-bar ftd-bar--stripe ftd-r11-tilebar"><span style="width: 0%" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" data-dz-uploadprogress></span></div>
                                <span class="ftd-ptile__err ftd-r11-msg"><i class="bx bx-error-circle ftd-r11-tileerr"></i><span data-dz-errormessage></span></span>
                                <button type="button" class="ftd-pill ftd-pill--danger ftd-pill--sm ftd-btn--block" data-dz-remove="">Remove file</button>
                            </div>
                        </div>`;
        },
        thumbnail: function(file, dataUrl) {
            var j, len, ref, thumbnailElement, is_featuredRef, is_featuredElement, add_to_apRef, add_to_apElement;
            file.id = file.upload ? file.upload.uuid : file.id;

            file.previewElement.classList.remove("dz-file-preview");

            if (file.manuallyAdded && file.id){
                this.asset_images_previewElements[file.id] = file.previewElement;
            }

            ref = file.previewElement.querySelectorAll("[data-dz-thumbnail-bg]");

            for (j = 0, len = ref.length; j < len; j++) {
                thumbnailElement = ref[j];
                thumbnailElement.alt = file.name;
                thumbnailElement.style.backgroundImage = 'url("' + dataUrl + '")';
            }

            /*
             * E7 (hand-off 2026-09-25, entry 7, the dated lines of 08:21Z and 08:23Z): THE ELEMENT IS
             * GUARDED, NOT THE LIST. `querySelectorAll()` answers a NodeList, which is truthy even when it
             * is empty, so a tile template without these inputs (the character and set tile of
             * `AddNewCharacter.vue` / `AddNewSet.vue`, `character_template()`) reached `[0].value` on
             * `undefined` and threw inside the Vue handler, and the T33b boundary in `app.js` took the page.
             * A tile that HAS the inputs (this mixin's own `template()`) is written exactly as before.
             * SHARED: every dialogue on this mixin, on every module, takes this.
             */
            is_featuredRef = file.previewElement.querySelectorAll("[data-dz-is_featured]");
            is_featuredElement = is_featuredRef[0];
            if (is_featuredElement){
                is_featuredElement.value = file.upload ? file.upload.uuid : file.id;
                is_featuredElement.checked = file.is_featured === 1;
            }
            add_to_apRef = file.previewElement.querySelectorAll("[data-dz-add_to_ap]");
            add_to_apElement = add_to_apRef[0];
            if (add_to_apElement){
                add_to_apElement.value = file.upload ? file.upload.uuid : file.id;
                add_to_apElement.checked = file.add_to_ap === 1;
            }

            let image_title = file.previewElement.querySelectorAll("[data-dz-image_title]");
            let image_titleElement = image_title[0];
            if (image_titleElement && file.name){
                image_titleElement.value = file.name;
            }

            let image_description = file.previewElement.querySelectorAll("[data-dz-image_description]");
            let image_descriptionElement = image_description[0];
            if (image_descriptionElement && file.description){
                image_descriptionElement.value = file.description;
            }

            return setTimeout(((function(_this) {
                return function() {
                    return file.previewElement.classList.add("dz-image-preview");
                };
            })(this)), 1);
        },

        plain_thumbnail: function(file, dataUrl) {
            var j, len, ref, thumbnailElement;

            file.previewElement.classList.remove("dz-file-preview");

            if (file.manuallyAdded && file.id){
                this.asset_images_previewElements[file.id] = file.previewElement;
            }

            ref = file.previewElement.querySelectorAll("[data-dz-thumbnail-bg]");

            for (j = 0, len = ref.length; j < len; j++) {
                thumbnailElement = ref[j];
                thumbnailElement.alt = file.name;
                thumbnailElement.style.backgroundImage = 'url("' + dataUrl + '")';
            }

            // E7 (entry 7, 08:21Z): the element is guarded, not the NodeList; see `thumbnail()` above.
            // This is the handler Add Character and Add Set bind, and their tile has neither input. SHARED.
            let image_title = file.previewElement.querySelectorAll("[data-dz-image_title]");
            let image_titleElement = image_title[0];
            if (image_titleElement && file.name){
                image_titleElement.value = file.name;
            }

            let image_description = file.previewElement.querySelectorAll("[data-dz-image_description]");
            let image_descriptionElement = image_description[0];
            if (image_descriptionElement && file.description){
                image_descriptionElement.value = file.description;
            }

            return setTimeout(((function(_this) {
                return function() {
                    return file.previewElement.classList.add("dz-image-preview");
                };
            })(this)), 1);
        },
        fileRemoved(file, error, xhr) {
            let file_id = file.id;
            if (!file_id){
                console.log("'no file id?'");
                console.log('no file id?');
                return;
            }

            
            if (this.cancelTokens && this.cancelTokens[file_id]){
                this.cancelTokens[file_id].cancel('Removed file?');
            }

            let index = this.addForm.filled_asset_images.map(function(e) { return e.id; }).indexOf(file_id);
            if (index > -1 && this.addForm.filled_asset_images.length > index){
                this.addForm.asset_images.splice(index, 1);
                this.addForm.filled_asset_images.splice(index, 1);
            }
            
            // shall check if enabled again?

            if (this.addForm.filled_asset_images.length === 0 || this.addForm.filled_asset_images.every(x => x.upload_progress === 100)){
                this.is_saving_enabled = true;
            }



        },
        fileAdded(file) {
            let file_id = file.hasOwnProperty('upload') ? file.upload.uuid : file.id;
            file.id = file_id;

            let has_s3_uuid = file.hasOwnProperty('s3_uuid');

            if (has_s3_uuid){
                if (file.previewElement) {
                    let progressElement = file.previewElement.querySelector("[data-dz-uploadprogress]");
                    progressElement.style.width = 100 + "%";
                    progressElement.setAttribute("aria-valuenow", 100);
                    progressElement.textContent = 'Done';
                }
            }

            if (!this.addForm.filled_asset_images){
                this.addForm.filled_asset_images = [];
            }
            let foundFile = null;
            let index = null;
            if (file_id){
                index = this.addForm.filled_asset_images.map(function(e) { return e.id; }).indexOf(file_id);
                foundFile = this.addForm.filled_asset_images[index];
            }

            if (foundFile && file.manuallyAdded){
                this.addForm.filled_asset_images[index] = file;
            }else{
                this.addForm.filled_asset_images.push(file);
                index = this.addForm.filled_asset_images.length - 1;
            }


            if (!has_s3_uuid){
                this.saveFileToS3(file, index);
            }

        },
        maxFilesExceeded(file){
            // todo: should show error message?
        },
        resetDropZone(){
            this.$refs.myVueDropzone.removeAllFiles();
            this.addForm.filled_asset_images = []
        },
        myResetDropZone(){
            this.$refs.myVueDropzone[0].removeAllFiles(true);
            let addForm = this.addForm;
            addForm.asset_images = [];
            addForm.filled_asset_images = [];
            this.addForm = Object.assign({}, addForm);
        },
    }
}
